import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Formik, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { Button, Form, Spinner } from 'lib/ui/components'
import { useProductSelector } from 'hooks/productSelector'
import { useIsMedium } from 'hooks/responsive'
import { PRODUCT_SELECTOR_CODE } from 'app/livbrite/resources/constants'
import { StyledForm, Actions } from 'app/livbrite/components/StyledForm'
import { ProgressBar } from 'components/ProgressBar'
import { Summary } from './components/Summary'
import { Title } from 'app/livbrite/scenes/Customer/components/Styled'
import { PublicLayout } from 'app/livbrite/components/PublicLayout'
import { QuestionWrapper } from './components/Styled'

export const WellnessSurvey = () => {
  const history = useHistory()
  const isTablet = useIsMedium()
  const {
    loading,
    questionData,
    resultsReady,
    results,
    step,
    submitAnswer,
    stepBack,
    showResults,
    questions,
    saveCustomerAnswers,
    savingAnswer
  } = useProductSelector({
    questionnaireCode: PRODUCT_SELECTOR_CODE.SURVEY
  })

  useEffect(() => {
    if (results && showResults) {
      history.push({
        pathname: 'wellness-survey/results',
        state: {
          results: results
        }
      })
    }
  }, [results, showResults, history])

  const { title, options, id: questionId, type, notes, limit } = questionData ?? {}
  const initialValues = {}
  const surveyLength = 5
  const handleSubmit = (values: Record<string, any>) => {
    submitAnswer(values)
  }
  const isFinalStep = step === surveyLength
  const isFirstStep = step === 0
  const showSummary = resultsReady
  const currentStep = step + 1
  const currentItem = showSummary ? surveyLength : currentStep
  const isTwoColumns = options?.length > 6 && !isTablet
  return (
    <PublicLayout className={showResults ? 'survey-results' : 'general-content'}>
      {savingAnswer && (
        <Spinner />
      )}
      <>
        <div>
          <Title>
            <ProgressBar
              totalCount={surveyLength}
              currentItem={currentItem}
            />
            <div>
              {showSummary ? (
                <span>
                  Summary
                </span>
              ) : (
                <span>
                  Question {currentStep} of {surveyLength}
                </span>
              )}
              Survey
            </div>
          </Title>
          <Formik
            key={`step-${step}`}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {(formikProps: any) => {
              const { values } = formikProps
              const hasNoValues = Object.values(values).every(x => (x === null || x === ''))
              const nextQuestionEnabled = type === 'text' ? !hasNoValues : values[questionId] && values[questionId]?.length > 0
              return (
                <Form.Wrapper>
                  <StyledForm>
                    {!showSummary ? (
                      <QuestionWrapper>
                        {loading ? (
                          <Spinner />
                        ) : (
                          <>
                            <div className='question__heading'>
                              <h3>{title}</h3>
                              {notes && (
                                <em>({notes})</em>
                              )}
                              {limit > 0 && limit !== options?.length && (
                                <p>Choose up to {limit} options</p>
                              )}
                            </div>
                            <div className={`question__options-wrapper ${isTwoColumns && 'columns-2'}`}>
                              {(options as any[])?.map((option: Record<string, any>) => {
                                const { label, value, child_options: childOptions } = option
                                const hasChildOptions = childOptions?.length > 0
                                const otherOption = label === 'Other'
                                const parentOptionChecked = values[questionId]?.find((optionValue: string) => optionValue === value.toString())
                                const isDisabled = limit > 0 && values[questionId]?.length >= limit

                                switch (type) {
                                  case 'text':
                                    return (
                                      <Field
                                        key={value}
                                        name={value}
                                        label={label}
                                        component={Form.Input}
                                      />
                                    )

                                  case 'multiselect':
                                    return (
                                      <>
                                        <label key={value} className={`checkbox ${isDisabled && 'disabled'}`}>
                                          <Field
                                            type='checkbox'
                                            name={questionId}
                                            value={value}
                                            checked={parentOptionChecked}
                                            disabled={isDisabled && !parentOptionChecked}
                                          />
                                          <span>{label}</span>
                                          {otherOption && parentOptionChecked && (
                                            <Field
                                              className='textareaExtra'
                                              as='textarea'
                                              name={`${questionId}-other`}
                                              value={values[`${questionId}-other`]}
                                              disabled={isDisabled && !parentOptionChecked}
                                            />
                                          )}
                                          {parentOptionChecked && hasChildOptions && (
                                            <div className='child-options'>
                                              {childOptions?.map((option: any) => {
                                                const childOptionChecked = values[questionId]?.find((optionValue: any) => optionValue === option.value.toString())

                                                return (
                                                  <>
                                                    <label
                                                      key={option.value}
                                                      className='checkbox'
                                                    >
                                                      <Field
                                                        type='checkbox'
                                                        name={questionId}
                                                        value={option.value}
                                                        checked={childOptionChecked}
                                                        disabled={isDisabled && !childOptionChecked}
                                                      />
                                                      <span>{option.label}</span>
                                                    </label>
                                                    {option.label.toLowerCase() === 'other' && childOptionChecked && (
                                                      <Field
                                                        className='textareaExtra'
                                                        as='textarea'
                                                        name={`${option.value}-other`}
                                                        value={values[`${option.value}-other`]}
                                                        disabled={isDisabled && !parentOptionChecked}
                                                      />
                                                    )}
                                                  </>
                                                )
                                              })}
                                            </div>
                                          )}
                                        </label>
                                      </>
                                    )

                                  default:
                                    return (
                                      <label key={value} className={`radio ${otherOption && 'other'}`}>
                                        <Field
                                          type='radio'
                                          name={questionId}
                                          value={value}
                                          checked={values[questionId] === value.toString()}
                                        />
                                        <span>{label}</span>
                                        {otherOption && (
                                          <Field
                                            type='text'
                                            name={`${value}-other`}
                                            className='text'
                                          />
                                        )}
                                      </label>
                                    )
                                }
                              })}
                            </div>
                          </>
                        )}
                      </QuestionWrapper>
                    ) : (
                      <Summary
                        questions={questions}
                        stepBack={stepBack}
                      />
                    )}
                    <Actions
                      alignRight
                    >
                      {!showSummary ? (
                        <>
                          {!isFirstStep && (
                            <Button
                              onClick={() => stepBack(questions[step-1])}
                              className='button__mutted'
                            >
                              <FormattedMessage id='survey.prev' />
                            </Button>
                          )}

                          {isFinalStep ? (
                            <Button
                              onClick={saveCustomerAnswers}
                            >
                              <FormattedMessage id='survey.submit' />
                            </Button>
                          ) : (
                            <Button
                              type='submit'
                              isDisabled={!nextQuestionEnabled}
                            >
                              <FormattedMessage id='survey.next' />
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          onClick={saveCustomerAnswers}
                        >
                          <FormattedMessage id='survey.results' />
                        </Button>
                      )}
                    </Actions>
                  </StyledForm>
                </Form.Wrapper>
              )
            }}
          </Formik>
        </div>
      </>
    </PublicLayout>
  )
}
